import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { LogIn } from "lucide-react";
import { Button, Card, Input } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/lib/api";

const DEFAULT_EMAIL = "";

interface LocationState {
  from?: { pathname: string };
}

export function Login() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as LocationState | null)?.from?.pathname ?? "/matters";

  const [email, setEmail] = useState(DEFAULT_EMAIL);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authed") {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError("Invalid email or password");
        } else if (err.code === "RATE_LIMITED") {
          setError(err.message);
        } else if (err.code === "NETWORK") {
          setError("Cannot reach the server. Check your connection.");
        } else {
          setError(err.message);
        }
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-stone-950 px-5 py-10">
      <Card variant="panel" padding="lg" className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <Link to="/" aria-label="Court Co-Pilot home">
            <img
              src="/lady-justice-emblem.webp"
              alt=""
              width="80"
              height="80"
              className="rounded-full"
            />
          </Link>
          <div>
            <h1 className="font-display text-2xl italic text-stone-100">
              Court Co-Pilot
            </h1>
            <p className="mt-1 text-xs uppercase tracking-[0.25em] text-stone-500">
              Sign in
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-1">
            <label htmlFor="email" className="text-sm text-stone-400">
              Email
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="password" className="text-sm text-stone-400">
              Password
            </label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </div>

          {error ? (
            <p
              role="alert"
              className="rounded-md border border-red-900/40 bg-red-950/30 px-3 py-2 text-xs text-red-300"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={submitting}
            leftIcon={<LogIn className="h-4 w-4" />}
          >
            {submitting ? "Signing in" : "Sign in"}
          </Button>
        </form>

        <p className="text-center text-[10px] uppercase tracking-[0.2em] text-stone-600">
          <Link to="/" className="text-stone-400 hover:text-amber-400">
            Back to homepage
          </Link>
        </p>
      </Card>
    </main>
  );
}
